import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';

import {
  ArrowLeft,
  CheckCircle,
  CircleNotch,
  Clock,
  WarningCircle,
  XCircle
} from '@phosphor-icons/react';
/**
 * Workflow Run Detail - Phase 10
 * View workflow execution status and results
 */
const WorkflowRunDetail = () => {
  const { run_id } = useParams();
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (activeOrganization && run_id) {
      fetchRun();
      // Poll for updates if running
      const interval = setInterval(() => {
        if (run && (run.status === 'running' || run.status === 'queued')) {
          fetchRun();
        }
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [activeOrganization, run_id]);

  const fetchRun = async () => {
    try {
      setLoading(true);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/workflow-runs/${run_id}`,
        {},
        activeOrganization.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        setRun(data);
      }
    } catch (err) {
      console.error('Error fetching workflow run:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'completed':
        return { icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-500/20', border: 'border-green-500/30', text: 'Completed' };
      case 'running':
        return { icon: CircleNotch, color: 'text-blue-400', bg: 'bg-blue-500/20', border: 'border-blue-500/30', text: 'Running', spin: true };
      case 'failed':
        return { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/30', text: 'Failed' };
      case 'stopped':
        return { icon: WarningCircle, color: 'text-yellow-400', bg: 'bg-yellow-500/20', border: 'border-yellow-500/30', text: 'Stopped' };
      case 'queued':
        return { icon: Clock, color: 'text-gray-400', bg: 'bg-gray-500/20', border: 'border-gray-500/30', text: 'Queued' };
      default:
        return { icon: Clock, color: 'text-gray-400', bg: 'bg-gray-500/20', border: 'border-gray-500/30', text: status };
    }
  };

  const getStepStatusInfo = (status) => {
    switch (status) {
      case 'completed':
        return { icon: CheckCircle, color: 'text-green-400' };
      case 'running':
        return { icon: CircleNotch, color: 'text-blue-400', spin: true };
      case 'failed':
        return { icon: XCircle, color: 'text-red-400' };
      case 'pending':
        return { icon: Clock, color: 'text-gray-400' };
      default:
        return { icon: Clock, color: 'text-gray-400' };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] p-6">
        <div className="max-w-5xl mx-auto">
          <div className="h-8 bg-gray-700 rounded w-48 mb-8 animate-pulse"></div>
          <div className="space-y-4">
            <div className="glass rounded-xl p-6 h-40 animate-pulse"></div>
            <div className="glass rounded-xl p-6 h-60 animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!run) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6">
        <div className="glass rounded-2xl p-8 max-w-md text-center">
          <h2 className="text-2xl font-bold text-white mb-3">Workflow Run Not Found</h2>
          <button
            onClick={() => navigate('/partner-pro/workspace/workflows')}
            className="px-6 py-3 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white rounded-lg hover:opacity-90"
          >
            Back to Workflows
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(run.status);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-6">
      <div className="max-w-5xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/partner-pro/workspace/workflows')}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          Back to Workflows
        </button>

        {/* Status Header */}
        <div className="glass rounded-xl p-6 border border-[rgba(255,255,255,0.1)] mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white mb-2">{run.workflow_name}</h1>
              <div className="flex items-center gap-4">
                <div className={`flex items-center gap-2 text-sm px-3 py-1 ${statusInfo.bg} ${statusInfo.color} border ${statusInfo.border} rounded`}>
                  <StatusIcon size={16} className={statusInfo.spin ? 'animate-spin' : ''} />
                  {statusInfo.text}
                </div>
                <span className="text-sm text-gray-400">
                  Started: {new Date(run.created_at).toLocaleString()}
                </span>
              </div>
              {run.completed_at && (
                <p className="text-sm text-gray-500 mt-2">
                  Completed: {new Date(run.completed_at).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Steps Timeline */}
        <div className="glass rounded-xl p-6 border border-[rgba(255,255,255,0.1)]">
          <h2 className="text-lg font-semibold text-white mb-4">Execution Timeline</h2>
          <div className="space-y-4">
            {run.step_results && run.step_results.length > 0 ? (
              run.step_results.map((stepResult, index) => {
                const stepStatusInfo = getStepStatusInfo(stepResult.status);
                const StepIcon = stepStatusInfo.icon;

                return (
                  <div
                    key={stepResult.step_id}
                    className="flex items-start gap-4 p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg"
                  >
                    {/* Step Number */}
                    <div className="flex-shrink-0 w-8 h-8 bg-[#6366F1] rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {index + 1}
                    </div>

                    {/* Step Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <StepIcon size={16} className={`${stepStatusInfo.color} ${stepStatusInfo.spin ? 'animate-spin' : ''}`} />
                        <h4 className="text-white font-medium">{stepResult.step_title}</h4>
                      </div>

                      {stepResult.started_at && (
                        <p className="text-xs text-gray-500 mb-1">
                          Started: {new Date(stepResult.started_at).toLocaleString()}
                        </p>
                      )}

                      {stepResult.completed_at && (
                        <p className="text-xs text-gray-500 mb-2">
                          Completed: {new Date(stepResult.completed_at).toLocaleString()}
                        </p>
                      )}

                      {/* Result */}
                      {stepResult.result && (
                        <div className="mt-2 p-3 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded text-sm">
                          <pre className="text-gray-300 whitespace-pre-wrap text-xs">
                            {JSON.stringify(stepResult.result, null, 2)}
                          </pre>
                        </div>
                      )}

                      {/* Error */}
                      {stepResult.error && (
                        <div className="mt-2 p-3 bg-red-500/10 border border-red-500/30 rounded">
                          <p className="text-sm text-red-300">{stepResult.error}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-8 text-center text-gray-400">
                <Clock size={48} className="mx-auto mb-3 opacity-50" />
                <p>No steps executed yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowRunDetail;
